import moment from 'moment-timezone';

/**
 * Preset identifiers for the date selector (single date).
 * Regular presets compute a date; 'selectDate' opens the single-date dialog.
 */
export type DateSelectorPresetId =
  | 'today'
  | 'yesterday'
  | 'dayMinus2'
  | 'dayMinus3'
  | 'dayMinus4'
  | 'dayMinus5'
  | 'dayMinus6'
  | 'selectDate';

/** Preset that opens the date picker dialog. */
export const CUSTOM_PRESET_ID: DateSelectorPresetId = 'selectDate';

export function isCustomDateSelectorPreset(id: DateSelectorPresetId): boolean {
  return id === CUSTOM_PRESET_ID;
}

/** Ordered preset ids: today, yesterday, 5 days in short format, then "Seleccionar fecha". */
export const DATE_SELECTOR_PRESET_ORDER: DateSelectorPresetId[] = [
  'today',
  'yesterday',
  'dayMinus2',
  'dayMinus3',
  'dayMinus4',
  'dayMinus5',
  'dayMinus6',
  'selectDate',
];

/**
 * Days-offset presets (today=0, yesterday=1, etc.) used to render the 5 "more days" in short format.
 * Index in this array matches dayMinus2..dayMinus6.
 */
export const DAY_OFFSET_PRESETS: DateSelectorPresetId[] = [
  'dayMinus2',
  'dayMinus3',
  'dayMinus4',
  'dayMinus5',
  'dayMinus6',
];

/**
 * Returns the single date (start of day in the given timezone) for a preset.
 * Returns null for 'selectDate'; callers use the dialog result.
 */
export function getPresetDate(
  presetId: DateSelectorPresetId,
  timezone: string,
  now?: Date,
): Date | null {
  if (presetId === 'selectDate') {
    return null;
  }

  const ref = now ? moment(now).tz(timezone) : moment().tz(timezone);

  switch (presetId) {
    case 'today': {
      return ref.clone().startOf('day').toDate();
    }
    case 'yesterday': {
      return ref.clone().subtract(1, 'day').startOf('day').toDate();
    }
    case 'dayMinus2': {
      return ref.clone().subtract(2, 'days').startOf('day').toDate();
    }
    case 'dayMinus3': {
      return ref.clone().subtract(3, 'days').startOf('day').toDate();
    }
    case 'dayMinus4': {
      return ref.clone().subtract(4, 'days').startOf('day').toDate();
    }
    case 'dayMinus5': {
      return ref.clone().subtract(5, 'days').startOf('day').toDate();
    }
    case 'dayMinus6': {
      return ref.clone().subtract(6, 'days').startOf('day').toDate();
    }
    default:
      return null;
  }
}

/**
 * Returns the preset id that corresponds to the given date (start of day in timezone).
 * Returns 'selectDate' if the date does not match any preset (e.g. custom or older than dayMinus6).
 */
export function getPresetIdFromDate(date: Date, timezone: string): DateSelectorPresetId {
  const dayStartTs = moment(date).tz(timezone).startOf('day').valueOf();
  for (const id of DATE_SELECTOR_PRESET_ORDER) {
    if (id === 'selectDate') continue;
    const presetDate = getPresetDate(id, timezone);
    if (presetDate && presetDate.getTime() === dayStartTs) return id;
  }
  return 'selectDate';
}
